// Configuración de consecuencias y recomendaciones por nivel y sintomatología
export const leveledContent = {
  tristeza: {
    LEVE: {
      consequences: [
        "Ligera disminución de energía en algunas actividades diarias",
        "Ocasionalmente puedes sentir que te cuesta más disfrutar",
        "Algunos momentos de reflexión sobre tu valía personal",
        "Pequeños cambios en tus patrones de sueño o alimentación"
      ],
      recommendations: [
        "Mantén rutinas de ejercicio regular, incluso caminatas de 20 minutos diarios para estimular neurotransmisores relacionados con el estado de ánimo",
        "Practica técnicas de mindfulness básicas para conectar con el momento presente y reducir la rumiación negativa",
        "Cuida especialmente tu higiene del sueño estableciendo horarios regulares, ya que el descanso adecuado es esencial para la regulación emocional",
        "Busca actividades que te resulten placenteras y prográmalas en tu semana, incluso si inicialmente no sientes motivación"
      ],
      color: "#4ecdc4", // Verde-azulado para nivel leve
      emergencyContact: false
    },
    MODERADO: {
      consequences: [
        "Disminución notable del interés en actividades que antes disfrutabas",
        "Cambios en el apetito o peso significativos",
        "Sensación frecuente de vacío o desesperanza",
        "Dificultad recurrente para concentrarse o tomar decisiones"
      ],
      recommendations: [
        "Considera buscar apoyo profesional con un psicólogo o terapeuta que se especialice en estado de ánimo",
        "Establece pequeñas metas diarias que puedas cumplir, focalizando en avances graduales más que en resultados",
        "Mantén contacto social regular, aunque no te apetezca inicialmente, priorizando personas que te hagan sentir comprendido/a",
        "Implementa técnicas de activación conductual: programa actividades específicas en momentos del día donde la tristeza sea más intensa",
        "Lleva un diario de pensamientos y emociones para identificar patrones que puedan estar manteniendo la tristeza"
      ],
      color: "#ff9800", // Naranja para nivel moderado
      emergencyContact: false
    },
    ALTO: {
      consequences: [
        "Pérdida significativa de interés o placer en casi todas las actividades",
        "Sentimientos intensos de inutilidad o culpa excesiva",
        "Pensamientos recurrentes de muerte o ideación suicida",
        "Dificultad severa para funcionar en actividades cotidianas",
        "Aislamiento social significativo"
      ],
      recommendations: [
        "Busca ayuda profesional de inmediato con un psicólogo o psiquiatra especializado en trastornos del estado de ánimo",
        "No te aísles, mantén contacto diario con personas de confianza que puedan dar seguimiento a tu bienestar",
        "Considera informar a un familiar o amigo cercano sobre tu estado actual y pide ayuda concreta (acompañamiento, recordatorios)",
        "Establece una rutina diaria simple y manejable, comenzando con lo más básico: horarios de comidas, breves salidas al exterior, higiene personal",
        "Aprende técnicas específicas de autocompasión para contrarrestar la autocrítica intensa que suele acompañar la tristeza profunda"
      ],
      color: "#ff6b6b", // Rojo para nivel alto
      emergencyContact: true
    }
  },
  ansiedad: {
    LEVE: {
      consequences: [
        "Tensión muscular ocasional, especialmente en cuello y hombros",
        "Preocupaciones pasajeras sobre temas cotidianos",
        "Algunas dificultades para relajarte completamente",
        "Nerviosismo ante situaciones nuevas o desafiantes"
      ],
      recommendations: [
        "Practica ejercicios de respiración diafragmática durante 5-10 minutos diarios, especialmente en momentos de tensión incipiente",
        "Mantén actividad física regular para reducir la activación del sistema nervioso simpático y liberar tensión acumulada",
        "Identifica y limita estimulantes como cafeína y azúcar que pueden potenciar síntomas físicos de ansiedad",
        "Establece una rutina de desconexión digital antes de dormir para facilitar la transición hacia el descanso",
        "Practica técnicas específicas de atención plena (mindfulness) que te permitan identificar tempranamente la aparición de síntomas físicos de ansiedad"
      ],
      color: "#4ecdc4",
      emergencyContact: false
    },
    MODERADO: {
      consequences: [
        "Preocupación excesiva y difícil de controlar sobre múltiples aspectos",
        "Síntomas físicos frecuentes como palpitaciones o tensión muscular",
        "Dificultades para concentrarte o sensación de mente en blanco",
        "Interferencia notable en tu descanso o vida social",
        "Evitación de situaciones que te generan malestar"
      ],
      recommendations: [
        "Considera consultar a un psicólogo especializado en terapia cognitivo-conductual, uno de los abordajes con mayor evidencia para el manejo de síntomas de ansiedad",
        "Practica técnicas de relajación progresiva muscular, especialmente útiles para reducir la tensión física asociada a la ansiedad",
        "Establece momentos específicos para 'preocuparte' limitados a 15 minutos al día, fuera de ese tiempo, posterga activamente las preocupaciones",
        "Aprende a identificar y cuestionar pensamientos catastróficos, especialmente aquellos que comienzan con '¿y si...?' negativos",
        "Practica la técnica 5-4-3-2-1 cuando notes síntomas de ansiedad: identifica 5 cosas que ves, 4 que puedes tocar, 3 que oyes, 2 que hueles y 1 que saboreas"
      ],
      color: "#ff9800",
      emergencyContact: false
    },
    ALTO: {
      consequences: [
        "Ataques de pánico frecuentes o intensos",
        "Evitación significativa de situaciones cotidianas",
        "Síntomas físicos severos que generan miedo o preocupación constante",
        "Alteraciones importantes en el sueño, apetito o funcionamiento general",
        "Sensación constante de amenaza o peligro inminente"
      ],
      recommendations: [
        "Busca ayuda profesional inmediata con un psicólogo o psiquiatra especializado en trastornos de ansiedad",
        "Durante episodios intensos, utiliza técnicas de grounding (como la 5-4-3-2-1) para reconectar con el momento presente y reducir la disociación",
        "Aprende a distinguir entre sensaciones físicas normales y síntomas de ansiedad para reducir la hipervigilancia corporal",
        "Evita el consumo de alcohol o sustancias para manejar los síntomas, ya que pueden empeorar la ansiedad a medio plazo",
        "Prioriza el autocuidado básico: alimentación, hidratación, descanso y evita la cafeína que puede exacerbar los síntomas físicos de ansiedad",
        "Diseña con ayuda profesional un plan de seguridad específico para momentos de crisis ansiosa"
      ],
      color: "#ff6b6b",
      emergencyContact: true
    }
  },
  estres: {
    LEVE: {
      consequences: [
        "Irritabilidad ocasional ante pequeñas contrariedades",
        "Fatiga al final del día más notable de lo habitual",
        "Pequeñas tensiones musculares, especialmente al final de la jornada",
        "Dificultad para relajarte completamente"
      ],
      recommendations: [
        "Incorpora mini-pausas de 5 minutos cada 2 horas en tu rutina para prevenir la acumulación de tensión",
        "Practica alguna técnica de relajación corta al final del día, específicamente diseñada para liberar la tensión acumulada",
        "Revisa tu organización del tiempo y prioriza tareas importantes, distinguiendo entre lo urgente y lo importante",
        "Realiza alguna actividad física moderada 3-4 veces por semana para liberar la tensión física asociada al estrés",
        "Identifica tus principales estresores y clasifícalos según sean controlables o no, para enfocar tu energía eficientemente"
      ],
      color: "#4ecdc4",
      emergencyContact: false
    },
    MODERADO: {
      consequences: [
        "Sensación de estar sobrepasado por las responsabilidades",
        "Irritabilidad frecuente o reacciones desproporcionadas",
        "Problemas de concentración o memoria",
        "Alteraciones del sueño como dificultad para conciliar o despertar temprano",
        "Síntomas físicos como dolores de cabeza o molestias digestivas"
      ],
      recommendations: [
        "Revisa y redefine tus límites personales y laborales, identificando áreas específicas donde necesitas establecer barreras más firmes",
        "Aprende a delegar tareas y a decir que no cuando sea necesario, reconociendo que esto es una habilidad esencial, no un fracaso",
        "Implementa técnicas específicas de manejo del estrés como mindfulness o yoga, dedicando al menos 15-20 minutos diarios",
        "Reserva tiempo exclusivo para actividades placenteras que sean verdaderamente recarga y no escape",
        "Considera buscar apoyo profesional si persisten los síntomas, preferiblemente con un especialista en estrés y burnout"
      ],
      color: "#ff9800",
      emergencyContact: false
    },
    ALTO: {
      consequences: [
        "Agotamiento físico y emocional profundo",
        "Síndrome de burnout o desgaste profesional",
        "Sintomatología física persistente (problemas digestivos, migrañas, etc.)",
        "Alteraciones significativas del sueño, apetito o libido",
        "Desmotivación profunda y sensación de pérdida de propósito"
      ],
      recommendations: [
        "Busca ayuda profesional especializada en estrés crónico y síndrome de burnout",
        "Evalúa seriamente la necesidad de un periodo de descanso o baja laboral, priorizando tu recuperación",
        "Establece límites firmes en todos los ámbitos de tu vida, siendo consciente de que recuperarte del estrés severo requiere cambios significativos",
        "Incorpora en tu rutina diaria técnicas de relajación profunda específicas para regular el sistema nervioso hiperactivado",
        "Comparte tu situación con tu entorno cercano para recibir apoyo, explicando claramente qué tipo de ayuda necesitas",
        "Simplifica radicalmente tu vida durante un tiempo, eliminando todas las actividades no esenciales para centrarte en la recuperación"
      ],
      color: "#ff6b6b",
      emergencyContact: true
    }
  },
  ruptura: {
    LEVE: {
      consequences: [
        "Momentos puntuales de tristeza al recordar la relación",
        "Algunas dudas sobre decisiones tomadas durante la relación",
        "Cierta incomodidad en situaciones sociales compartidas",
        "Pensamientos ocasionales sobre tu ex pareja"
      ],
      recommendations: [
        "Permite sentir tus emociones sin juzgarlas ni reprimirlas, validando que son parte normal del proceso de duelo",
        "Mantén tus rutinas y actividades habituales, añadiendo gradualmente nuevas experiencias que te ayuden a redefinir tu cotidianidad",
        "Refuerza el contacto con amistades y familiares que no estén directamente vinculados a la relación finalizada",
        "Practica el autocuidado y dedica tiempo a actividades que disfrutes personalmente y que quizás habías dejado de lado",
        "Crea un espacio limitado para la reflexión sobre la relación, evitando la rumiación constante"
      ],
      color: "#4ecdc4",
      emergencyContact: false
    },
    MODERADO: {
      consequences: [
        "Pensamientos recurrentes sobre la relación y su ruptura",
        "Dificultad para concentrarte en tus actividades diarias",
        "Cambios en tus patrones de sueño o alimentación",
        "Sentimientos intensos de tristeza o enfado",
        "Tendencia a idealizar la relación pasada"
      ],
      recommendations: [
        "Establece límites claros con tu ex pareja (contacto mínimo necesario o pausa temporal), incluyendo redes sociales",
        "Crea un diario de emociones para procesar tus sentimientos, distinguiendo entre el dolor de la pérdida y aspectos reales de la relación",
        "Busca algún grupo de apoyo o terapia especializada en duelo por ruptura, donde puedas compartir experiencias",
        "Estructura tu rutina diaria con actividades nutritivas alternando momentos de conexión social y tiempo personal",
        "Practica técnicas específicas para manejar los pensamientos intrusivos sobre la relación, como la defusión cognitiva",
        "Explora nuevos intereses o recupera antiguos que te ayuden a reconectar con tu identidad individual"
      ],
      color: "#ff9800",
      emergencyContact: false
    },
    ALTO: {
      consequences: [
        "Intenso dolor emocional que interfiere significativamente en tu vida",
        "Incapacidad para funcionar normalmente en áreas importantes",
        "Aislamiento social prolongado",
        "Comportamientos autodestructivos o de riesgo",
        "Ideación recurrente sobre reconciliación o contacto constante"
      ],
      recommendations: [
        "Busca ayuda profesional especializada en duelo o rupturas traumáticas, priorizando terapeutas con experiencia en dependencia emocional",
        "Establece contacto cero con tu ex pareja temporalmente si es posible, como parte de tu proceso de recuperación y no como castigo",
        "Apóyate en tu red de confianza y comparte cómo te sientes, pidiendo ayuda específica en momentos críticos",
        "Enfócate en el día a día con pequeñas metas alcanzables, evitando proyectar demasiado hacia el futuro",
        "Practica técnicas de regulación emocional como la respiración diafragmática para momentos de crisis emocional intensa",
        "Implementa estrategias específicas para manejar pensamientos obsesivos sobre la relación, posiblemente con ayuda profesional",
        "Establece un plan de seguridad si experimentas impulsos autodestructivos o de contacto compulsivo"
      ],
      color: "#ff6b6b",
      emergencyContact: true
    }
  },
  autoestima: {
    LEVE: {
      consequences: [
        "Dudas ocasionales sobre tus capacidades en situaciones nuevas o desafiantes",
        "Sensibilidad moderada a las críticas o al rechazo en entornos específicos",
        "Tendencia a compararte con otros en aspectos particulares de tu vida",
        "Dificultad para reconocer completamente tus logros o aceptar cumplidos",
        "Momentos puntuales de inseguridad que no interfieren significativamente con tu vida diaria"
      ],
      recommendations: [
        "Practica el diálogo interno positivo y realista, cuestionando específicamente los pensamientos autocríticos",
        "Lleva un registro diario de logros y cualidades positivas, por pequeños que parezcan",
        "Establece pequeños desafíos para salir de tu zona de confort, incrementando gradualmente su dificultad",
        "Rodéate de personas que te valoren y aprecien genuinamente, identificando relaciones que refuerzan tu valía",
        "Aprende a recibir cumplidos sin restarles importancia, practicando simplemente decir 'gracias'",
        "Practica la autocompasión consciente cuando cometas errores, tratándote como tratarías a un amigo"
      ],
      color: "#4ecdc4",
      emergencyContact: false
    },
    MODERADO: {
      consequences: [
        "Diálogo interno autocrítico persistente que erosiona tu confianza",
        "Dificultad significativa para reconocer tus fortalezas, habilidades y cualidades positivas",
        "Evitación de situaciones donde percibes riesgo de evaluación o juicio negativo",
        "Necesidad constante de aprobación y validación externa para sentirte valioso/a",
        "Tendencia a compararte desfavorablemente con otros de forma habitual",
        "Dificultad para expresar opiniones o necesidades por miedo al rechazo",
        "Sensación de impostor en tus logros profesionales o personales"
      ],
      recommendations: [
        "Considera la terapia cognitivo-conductual para trabajar creencias limitantes relacionadas con tu valía personal",
        "Practica técnicas específicas de autocompasión y mindfulness para reducir la autocrítica destructiva",
        "Establece límites saludables en tus relaciones, especialmente con personas que refuerzan tu autoimagen negativa",
        "Identifica y cuestiona sistemáticamente tu autocrítica destructiva, buscando evidencia que la contradiga",
        "Desarrolla habilidades en áreas donde puedas experimentar pequeños logros consistentes",
        "Aprende a validarte a ti mismo en lugar de buscar constantemente aprobación externa",
        "Reconoce patrones familiares o de relaciones pasadas que puedan haber contribuido a tu baja autoestima"
      ],
      color: "#ff9800",
      emergencyContact: false
    },
    ALTO: {
      consequences: [
        "Creencia profundamente arraigada de inadecuación o falta de valía personal en casi todos los aspectos de tu vida",
        "Autocrítica destructiva constante con pensamientos frecuentes de autodesprecio e invalidación",
        "Evitación significativa de situaciones sociales, profesionales o académicas por miedo al rechazo",
        "Patrones relacionales disfuncionales marcados por la dependencia excesiva, sumisión o aislamiento",
        "Sabotaje inconsciente de oportunidades o relaciones por sentir que no las mereces",
        "Perfeccionismo paralizante que impide la acción o la finalización de proyectos",
        "Dificultad severa para tomar decisiones sin la validación de otros",
        "Posibles conductas autodestructivas como mecanismo de afrontamiento del malestar emocional",
        "Sistema de creencias rígido sobre tus supuestas limitaciones o defectos"
      ],
      recommendations: [
        "Busca ayuda profesional especializada en autoestima y patrones relacionales disfuncionales",
        "Aprende a identificar y desafiar las distorsiones cognitivas, especialmente aquellas relacionadas con tu valía personal",
        "Establece una práctica diaria de autocompasión, trabajando específicamente con la vergüenza y el autodesprecio",
        "Revisa tus relaciones y aléjate de aquellas que sean tóxicas o que refuercen tu visión negativa de ti mismo",
        "Construye pequeños éxitos diarios para aumentar tu confianza, empezando por metas muy accesibles",
        "Desarrolla tu asertividad para expresar necesidades y límites de forma saludable",
        "Trabaja con un profesional en el origen de tus creencias negativas sobre ti mismo",
        "Considera grupos de apoyo específicos para problemas de autoestima y autoaceptación"
      ],
      color: "#ff6b6b",
      emergencyContact: true
    }
  },
  insomnio: {
    LEVE: {
      consequences: [
        "Fatiga ocasional durante el día",
        "Algunas dificultades para conciliar el sueño",
        "Despertares ocasionales durante la noche",
        "Leve impacto en tu energía o estado de ánimo"
      ],
      recommendations: [
        "Establece un horario regular de sueño, incluso los fines de semana, para regular tu ritmo circadiano",
        "Crea una rutina relajante antes de dormir (sin pantallas) de al menos 30 minutos",
        "Evita cafeína y alcohol en las horas previas al descanso, idealmente 6 horas antes para la cafeína",
        "Asegura que tu dormitorio sea un espacio confortable, oscuro, silencioso y ligeramente fresco",
        "Practica técnicas de relajación específicas para el sueño, como el escáner corporal o la respiración 4-7-8",
        "Limita la exposición a luz azul de pantallas al menos 1-2 horas antes de acostarte"
      ],
      color: "#4ecdc4",
      emergencyContact: false
    },
    MODERADO: {
      consequences: [
        "Fatiga significativa durante el día que afecta tu rendimiento",
        "Dificultad frecuente para conciliar o mantener el sueño",
        "Irritabilidad o cambios de humor notables",
        "Problemas de concentración o memoria",
        "Inicio de patrones negativos alrededor del sueño"
      ],
      recommendations: [
        "Implementa técnicas de higiene del sueño de forma consistente y estricta, como parte de un programa completo",
        "Practica la relajación muscular progresiva antes de dormir para reducir la tensión física acumulada",
        "Considera utilizar apps específicas para mejorar el sueño con meditaciones guiadas para insomnio",
        "Aplica técnicas de restricción del sueño y control de estímulos bajo orientación profesional",
        "Realiza ejercicio regular pero no en las horas cercanas al descanso (al menos 3-4 horas antes)",
        "Si persiste, consulta con un profesional especializado en trastornos del sueño",
        "Mantén un diario detallado de sueño para identificar patrones y desencadenantes específicos"
      ],
      color: "#ff9800",
      emergencyContact: false
    },
    ALTO: {
      consequences: [
        "Insomnio crónico que afecta significativamente tu calidad de vida",
        "Agotamiento extremo y problemas para funcionar normalmente",
        "Ansiedad anticipatoria intensa relacionada con el momento de dormir",
        "Síntomas físicos como dolores de cabeza, problemas digestivos",
        "Riesgo de desarrollar otros problemas de salud mental"
      ],
      recommendations: [
        "Busca ayuda profesional con un especialista en medicina del sueño o un psicólogo especializado en insomnio",
        "Considera la terapia cognitivo-conductual para el insomnio (TCC-I), el tratamiento con mayor evidencia científica",
        "Mantén un diario de sueño detallado para identificar patrones y compartirlo con el profesional",
        "Evita las siestas durante el día, incluso si te sientes cansado, para mejorar la presión de sueño nocturna",
        "No te automediques, consulta siempre con un profesional sobre cualquier ayuda para dormir",
        "Implementa técnicas de desactivación cognitiva para manejar la rumiación mental al acostarte",
        "Practica la aceptación del insomnio ocasional sin añadir más estrés a la situación",
        "Diseña un plan integral con ayuda profesional que combine técnicas comportamentales, cognitivas y de atención plena"
      ],
      color: "#ff6b6b",
      emergencyContact: true
    }
  }
};
